import { fabric } from "fabric";
import { store } from "@/store";

import { undoActionStack } from "@/canvas-api/ActionStack";

import { ActionTypes } from "@/store/actions";
import { Property } from "@/types";

export const updateActiveObject = (activeObject: fabric.Object, property: Property): void => {
  const isGroup = (obj: fabric.Object): obj is fabric.Group => {
    const tmp = obj as fabric.Group;

    if (tmp._objects) {
      return true;
    } else {
      return false;
    }
  };

  switch (property.name) {
    case "fontFamily": {
      const iTextObject = activeObject as fabric.IText;
      
      undoActionStack.push({
        id: iTextObject.id as number,
        type: "fontFamily",
        object: fabric.util.object.clone(iTextObject),
      });

      iTextObject.set({
        fontFamily: property.value as string,
        dirty: true,
      });

      store.dispatch(ActionTypes.SET_CANVAS_ACTIVE_OBJECT, iTextObject);
      break;
    }
    case "fontSize": {
      const iTextObject = activeObject as fabric.IText;

      undoActionStack.push({
        id: iTextObject.id as number,
        type: "fontSize",
        object: fabric.util.object.clone(iTextObject),
      });

      iTextObject.set({
        fontSize: property.value as number,
        scaleX: 1,
        scaleY: 1,
        dirty: true,
      });

      store.dispatch(ActionTypes.SET_CANVAS_ACTIVE_OBJECT, iTextObject);
      break;
    }
    case "fontWeight": {
      const iTextObject = activeObject as fabric.IText;

      undoActionStack.push({
        id: iTextObject.id as number,
        type: "fontWeight",
        object: fabric.util.object.clone(iTextObject),
      });

      iTextObject.set({
        fontWeight: property.value as string,
        dirty: true,
      });

      store.dispatch(ActionTypes.SET_CANVAS_ACTIVE_OBJECT, iTextObject);
      break;
    }
    case "fontStyle": {
      const iTextObject = activeObject as fabric.IText;

      undoActionStack.push({
        id: iTextObject.id as number,
        type: "fontStyle",
        object: fabric.util.object.clone(iTextObject),
      });

      iTextObject.set({
        fontStyle: property.value as "italic" | "normal",
        dirty: true,
      });

      store.dispatch(ActionTypes.SET_CANVAS_ACTIVE_OBJECT, iTextObject);
      break;
    }
    case "underline": {
      const iTextObject = activeObject as fabric.IText;

      undoActionStack.push({
        id: iTextObject.id as number,
        type: "underline",
        object: fabric.util.object.clone(iTextObject),
      });

      iTextObject.set({
        underline: property.value as boolean,
        dirty: true,
      });

      store.dispatch(ActionTypes.SET_CANVAS_ACTIVE_OBJECT, iTextObject);
      break;
    }
    case "textAlign": {
      const iTextObject = activeObject as fabric.IText;

      undoActionStack.push({
        id: iTextObject.id as number,
        type: "textAlign",
        object: fabric.util.object.clone(iTextObject),
      });

      iTextObject.set({
        textAlign: property.value as string,
        dirty: true,
      });

      store.dispatch(ActionTypes.SET_CANVAS_ACTIVE_OBJECT, iTextObject);
      break;
    }
    case "text": {
      const iTextObject = activeObject as fabric.IText;

      iTextObject.set({
        text: property.value as string,
        dirty: true,
      });

      store.dispatch(ActionTypes.SET_CANVAS_ACTIVE_OBJECT, iTextObject);
      break;
    }
    case "opacity": {
      const currentObject = activeObject as fabric.Object;

      undoActionStack.push({
        id: currentObject.id as number,
        type: "opacity",
        object: fabric.util.object.clone(currentObject),
      });

      currentObject.set({
        opacity: property.value as number,
        dirty: true,
      });

      store.dispatch(ActionTypes.SET_CANVAS_ACTIVE_OBJECT, currentObject);
      break;
    }
    case "strokeWidth": {
      const currentObject = activeObject as fabric.Object;

      if (isGroup(currentObject)) {
        //Need to make actual deep clones for groups, shallow will carry over children object changes to the object added to the undo/redo stack
        currentObject.clone(function(clone: fabric.Group) {
          undoActionStack.push({
            id: currentObject.id as number,
            type: "strokeWidth",
            object: clone,
          });
        });

        currentObject._objects.forEach((obj) => {
          obj.set({
            strokeWidth: property.value as number,
            dirty: true,
          });
        });
      } else {
        undoActionStack.push({
          id: currentObject.id as number,
          type: "strokeWidth",
          object: fabric.util.object.clone(currentObject),
        });

        currentObject.set({
          strokeWidth: property.value as number,
          dirty: true,
        });
      }

      store.dispatch(ActionTypes.SET_CANVAS_ACTIVE_OBJECT, currentObject);
      break;
    }
    case "fill": {
      const currentObject = activeObject as fabric.Object;

      if (isGroup(currentObject)) {
        //Need to make actual deep clones for groups, shallow will carry over children object changes to the object added to the undo/redo stack
        currentObject.clone(function(clone: fabric.Group) {
          undoActionStack.push({
            id: currentObject.id as number,
            type: "fill",
            object: clone,
          });
        });

        currentObject._objects.forEach((obj) => {
          obj.set({
            fill: property.value as string,
            dirty: true,
          });
        });
      } else {
        undoActionStack.push({
          id: currentObject.id as number,
          type: "fill",
          object: fabric.util.object.clone(currentObject),
        });

        currentObject.set({
          fill: property.value as string,
          dirty: true,
        });
      }

      store.dispatch(ActionTypes.SET_CANVAS_ACTIVE_OBJECT, currentObject);
      break;
    }
    case "stroke": {
      const currentObject = activeObject as fabric.Object;

      if (isGroup(currentObject)) {
        //Need to make actual deep clones for groups, shallow will carry over children object changes to the object added to the undo/redo stack
        currentObject.clone(function(clone: fabric.Group) {
          undoActionStack.push({
            id: currentObject.id as number,
            type: "stroke",
            object: clone,
          });
        });

        currentObject._objects.forEach((obj) => {
          obj.set({
            stroke: property.value as string,
            dirty: true,
          });
        });
      } else {
        undoActionStack.push({
          id: currentObject.id as number,
          type: "stroke",
          object: fabric.util.object.clone(currentObject),
        });

        currentObject.set({
          stroke: property.value as string,
          dirty: true,
        });
      }

      store.dispatch(ActionTypes.SET_CANVAS_ACTIVE_OBJECT, currentObject);
      break;
    }
    case "scaleX": {
      const currentObject = activeObject as fabric.Object;

      if (isGroup(currentObject)) {
        //Need to make actual deep clones for groups, shallow will carry over children object changes to the object added to the undo/redo stack
        currentObject.clone(function(clone: fabric.Group) {
          undoActionStack.push({
            id: currentObject.id as number,
            type: "scaleX",
            object: clone,
          });
        });

        currentObject._objects.forEach((obj) => {
          obj.set({
            scaleX: property.value as number,
            dirty: true,
          });
        });
      } else {
        undoActionStack.push({
          id: currentObject.id as number,
          type: "scaleX",
          object: fabric.util.object.clone(currentObject),
        });

        currentObject.set({
          scaleX: property.value as number,
          dirty: true,
        });
      }

      store.dispatch(ActionTypes.SET_CANVAS_ACTIVE_OBJECT, currentObject);
      break;
    }
    case "scaleY": {
      const currentObject = activeObject as fabric.Object;

      if (isGroup(currentObject)) {
        //Need to make actual deep clones for groups, shallow will carry over children object changes to the object added to the undo/redo stack
        currentObject.clone(function(clone: fabric.Group) {
          undoActionStack.push({
            id: currentObject.id as number,
            type: "scaleY",
            object: clone,
          });
        });

        currentObject._objects.forEach((obj) => {
          obj.set({
            scaleY: property.value as number,
            dirty: true,
          });
        });
      } else {
        undoActionStack.push({
          id: currentObject.id as number,
          type: "scaleY",
          object: fabric.util.object.clone(currentObject),
        });

        currentObject.set({
          scaleY: property.value as number,
          dirty: true,
        });
      }
      
      store.dispatch(ActionTypes.SET_CANVAS_ACTIVE_OBJECT, currentObject);
      break;
    }
    case "strokeDashArray": {
      const currentObject = activeObject as fabric.Object;

      undoActionStack.push({
        id: currentObject.id as number,
        type: "strokeDashArray",
        object: fabric.util.object.clone(currentObject),
      });

      currentObject.set({
        strokeDashArray: property.value as number[],
        dirty: true,
      });

      store.dispatch(ActionTypes.SET_CANVAS_ACTIVE_OBJECT, currentObject);
      break;
    }
  }
};
